import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { trendValenceClasses, type TrendValence } from '../lib/trend';
import { cn } from '../lib/utils';

export type MetricTrend = 'up' | 'down' | 'neutral' | 'flat';

export interface MetricCardProps {
  label: string;
  value: string | number;
  trend?: MetricTrend;
  /** Omitted valence is intentionally neutral. Direction alone never implies success. */
  trendValence?: TrendValence;
  trendValue?: string;
  icon?: React.ReactNode;
  className?: string;
  onClick?: () => void;
}

const trendConfig = { up: TrendingUp, down: TrendingDown, neutral: Minus, flat: Minus };

export function MetricCard({
  label,
  value,
  trend,
  trendValence = 'neutral',
  trendValue,
  icon,
  className,
  onClick,
}: MetricCardProps) {
  const TrendIcon = trend ? trendConfig[trend] : null;

  return (
    <div
      className={cn(
        'relative min-w-0 overflow-hidden rounded-card border border-border bg-surface-sheet p-4 transition-interactive duration-200 ease-out',
        onClick && 'cursor-pointer hover:border-statura-green-accessible/30 hover:bg-surface-soft',
        className,
      )}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
    >
      <div className="flex min-w-0 items-start justify-between gap-3">
        <div className="min-w-0 space-y-2">
          <p className="min-w-0 break-words font-body text-body-sm text-text-muted">{label}</p>
          <p className="min-w-0 break-words font-body text-metric text-text-heading tabular-nums">
            {value}
          </p>
        </div>
        {icon && (
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-control border border-statura-green-accessible/15 bg-statura-green-accessible/10 text-statura-green-accessible [&_svg]:h-4 [&_svg]:w-4">
            {icon}
          </div>
        )}
      </div>

      {TrendIcon && trendValue && (
        <div className="mt-4 flex min-w-0 flex-wrap items-center gap-1.5">
          <span
            className={cn(
              'inline-flex items-center gap-1 rounded px-2.5 py-1 text-xs font-semibold',
              trendValenceClasses[trendValence],
            )}
          >
            <TrendIcon className="h-3 w-3" aria-hidden="true" />
            {trendValue}
          </span>
        </div>
      )}
    </div>
  );
}
